import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function GenerateAssignments({ formData, updateFormData }) {
  const { t } = useTranslation();
  const { participants, rules, eventType, familyUnits, sameGiftNumberForAll, giftsByType, numberOfGifts, assignments, unaccountedPresents, totalPresents, showOnlyGivers } = formData;

  useEffect(() => {
    const givers = (eventType === 'kris-kindle' && showOnlyGivers) ? participants.filter(p => p.isGiver) : participants;
    const totalGiftsToBuy = (() => {
      if (sameGiftNumberForAll) {
        return participants.length * (numberOfGifts || 1);
      } else {
        const ageGroups = {
          'Adult': 0,
          '13-17': 0,
          '8-12': 0,
          '4-7': 0,
          '0-3': 0,
        };

        participants.forEach(p => {
          ageGroups[p.age]++;
        });

        let total = 0;
        for (const age in ageGroups) {
          total += ageGroups[age] * (giftsByType[age] || 1);
        }
        return total;
      }
    })();

    if (givers.length > 0) {
      const basePresentsPerGiver = Math.floor(totalGiftsToBuy / givers.length);
      const remainder = totalGiftsToBuy % givers.length;

      const initialAssignments = {};
      givers.forEach(giver => {
        initialAssignments[giver.name] = basePresentsPerGiver;
      });

      // Only update if assignments don't exist, givers changed, or total changed
      // Don't update if user has manually adjusted assignments
      const needsUpdate = !assignments ||
        Object.keys(initialAssignments).length !== Object.keys(assignments).length ||
        totalPresents !== totalGiftsToBuy;

      if (needsUpdate) {
        updateFormData({
          assignments: initialAssignments,
          unaccountedPresents: remainder,
          totalPresents: totalGiftsToBuy
        });
      }
    }
  }, [participants, sameGiftNumberForAll, giftsByType, numberOfGifts, eventType, showOnlyGivers]);

  const handleDrop = (giverName) => {
    if (unaccountedPresents > 0) {
      const currentAssignments = assignments[giverName] || 0;
      const newAssignments = { ...assignments, [giverName]: currentAssignments + 1 };
      updateFormData({ assignments: newAssignments, unaccountedPresents: unaccountedPresents - 1 });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleCountChange = (giverName, newCount) => {
    const oldCount = assignments[giverName] || 0;
    const diff = newCount - oldCount;

    // Ensure newCount is not negative
    if (newCount < 0) return;

    // Check if we have enough unaccounted presents for increases
    if (diff > 0 && unaccountedPresents < diff) return;

    const newAssignments = { ...assignments, [giverName]: newCount };
    updateFormData({ assignments: newAssignments, unaccountedPresents: unaccountedPresents - diff });
  };

  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
  };

  const givers = (eventType === 'kris-kindle' && showOnlyGivers) ? participants.filter(p => p.isGiver) : participants;

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          {t('generateAssignments.infoBanner')}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Givers Assignment Section */}
        <div className="flex-1 lg:w-2/3">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('generateAssignments.giftAssignmentsTitle')}</h3>
            <div className="space-y-3">
              {givers.map(giver => (
                <div
                  key={giver.name}
                  className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg bg-gradient-to-r from-gray-50 to-white hover:border-blue-300 transition-all"
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(giver.name)}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg mr-4 shadow-md">
                      {giver.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800 text-lg">{giver.name}</span>
                      <div className="text-xs text-gray-500">
                        {(assignments[giver.name] || 0) === 1
                          ? t('generateAssignments.giftCount.singular')
                          : t('generateAssignments.giftCount.plural', { count: assignments[giver.name] || 0 })
                        }
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => handleCountChange(giver.name, (assignments[giver.name] || 0) - 1)}
                      className="w-9 h-9 flex items-center justify-center border-2 border-gray-300 rounded-lg bg-white hover:bg-red-50 hover:border-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold text-gray-700"
                      disabled={(assignments[giver.name] || 0) <= 0}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={assignments[giver.name] || 0}
                      onChange={(e) => handleCountChange(giver.name, parseInt(e.target.value, 10) || 0)}
                      className="w-16 text-center border-2 border-gray-300 rounded-lg py-2 font-bold text-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => handleCountChange(giver.name, (assignments[giver.name] || 0) + 1)}
                      className="w-9 h-9 flex items-center justify-center border-2 border-gray-300 rounded-lg bg-white hover:bg-green-50 hover:border-green-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold text-gray-700"
                      disabled={unaccountedPresents <= 0}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Summary Section */}
        <div className="lg:w-1/3">
          <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm sticky top-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('generateAssignments.summary.title')}</h3>
            <div className="space-y-4">
              {/* Total Presents */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-purple-700 uppercase tracking-wide">{t('generateAssignments.summary.totalPresents')}</div>
                    <div className="text-3xl font-bold text-purple-900 mt-1">{totalPresents}</div>
                  </div>
                  <div className="text-4xl">🎁</div>
                </div>
              </div>

              {/* Total Givers */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-blue-700 uppercase tracking-wide">{t('generateAssignments.summary.totalGivers')}</div>
                    <div className="text-3xl font-bold text-blue-900 mt-1">{givers.length}</div>
                  </div>
                  <div className="text-4xl">👥</div>
                </div>
              </div>

              {/* Gifts per Giver */}
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-indigo-700 uppercase tracking-wide">{t('generateAssignments.summary.basePerGiver')}</div>
                    <div className="text-3xl font-bold text-indigo-900 mt-1">
                      {givers.length > 0 ? Math.floor(totalPresents / givers.length) : 0}
                    </div>
                  </div>
                  <div className="text-4xl">🎯</div>
                </div>
              </div>

              {/* Leftover Gifts */}
              {unaccountedPresents > 0 && (
                <div className="p-4 bg-gradient-to-br from-yellow-50 to-amber-100 rounded-lg border-2 border-yellow-300 shadow-md">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-bold text-yellow-900">{t('generateAssignments.summary.leftoverGifts')}</div>
                      <div className="text-2xl font-bold text-yellow-900 mt-1">{unaccountedPresents}</div>
                    </div>
                    <div className="text-3xl">⚠️</div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3 p-3 bg-white/60 rounded-lg border border-yellow-200">
                    {Array.from({ length: unaccountedPresents }).map((_, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={handleDragStart}
                        className="text-3xl cursor-move hover:scale-125 transition-transform active:scale-95"
                        title={t('generateAssignments.summary.dragToAssign')}
                      >
                        🎁
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-yellow-800 bg-yellow-100/50 p-2 rounded">
                    {t('generateAssignments.summary.dragTip')}
                  </div>
                </div>
              )}

              {/* All Gifts Assigned */}
              {unaccountedPresents === 0 && (
                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg border-2 border-green-300 shadow-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-green-900">{t('generateAssignments.summary.allAssigned')}</div>
                      <div className="text-xs text-green-700 mt-1">
                        {t('generateAssignments.summary.allAssignedDescription')}
                      </div>
                    </div>
                    <div className="text-3xl">✅</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenerateAssignments;